import { Scope } from '@talpor/react-context-manager';

import api, { request } from '../../api';
import { IStore } from '../index';

import { initialAuthStore } from './store';

export interface ILoginForm {
  identifier: string;
  password: string;
}

export interface IRegisterForm {
  email: string;
  password: string;
  repeatPassword: string;
}

export interface IResetForm {
  code: string;
  password: string;
  passwordConfirmation: string;
}

export interface IAuthActions extends Scope<IStore> {
  login: (state: IStore) => (data: ILoginForm) => Promise<IStore>;
  forgot: (state: IStore) => (email: string) => Promise<IStore>;
  reset: (state: IStore) => (data: IResetForm) => Promise<IStore>;
  register: (state: IStore) => (data: IRegisterForm) => Promise<IStore>;
  logout: (state: IStore) => () => Promise<IStore>;
  me: (state: IStore) => () => Promise<IStore>;
  slack: (state: IStore) => (token: string, language: string) => Promise<IStore>;
  microsoft: (state: IStore) => (token: string, language: string) => Promise<IStore>;
}

const persistData = (authState: any): void => {
  localStorage.setItem('auth', JSON.stringify(authState));
};

export const authActions: IAuthActions = {
  login: (state: IStore) => async (data: ILoginForm) => {
    const response = await request.post(api.login, data);

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: response.error.body.data[0].messages[0].message,
            type: 'error',
          },
        },
      };
    }

    const { jwt, user } = response;
    const auth = { token: jwt, isAuthenticated: true, user };
    persistData(auth);

    return {
      ...state,
      auth,
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: false, message: '', type: '' },
      },
    };
  },
  forgot: (state: IStore) => async (email: string) => {
    const response = await request.post(api.forgot, { email });

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong. Please try again later or contact us',
            type: 'error',
          },
        },
      };
    }

    return {
      ...state,
      misc: {
        loading: false,
        notification: {
          status: true,
          message:
            'If the email exists in our records, you will receive an email with the instructions',
          type: 'success',
        },
      },
    };
  },
  reset: (state: IStore) => async (data: IResetForm) => {
    const response = await request.post(api.reset, data, true);

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: 'Something went wrong with the reset. Please try again.',
            type: 'error',
          },
        },
      };
    }

    return {
      ...state,
      loading: false,
      notification: {
        status: true,
        message: 'Your password has successfully changed',
        type: 'success',
      },
    };
  },
  register: (state: IStore) => async (data: IRegisterForm): Promise<IStore> => {
    const response = await request.post(api.register, {
      username: data.email,
      email: data.email,
      password: data.password,
    });

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: response.error.body.data[0].messages[0].message,
            type: 'error',
          },
        },
      };
    }

    const { jwt, user } = response;
    const auth = { token: jwt, isAuthenticated: true, user };
    persistData(auth);

    return {
      ...state,
      auth,
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: false, message: '', type: '' },
      },
    };
  },
  logout: () => async (): Promise<IStore> => {
    localStorage.removeItem('auth');
    localStorage.removeItem('currentTeam');

    return {
      auth: initialAuthStore,
    } as IStore;
  },
  me: (state: IStore) => async (): Promise<IStore> => {
    const response = await request.get(api.me);

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message:
              'You must to confirm your email before continue using Sensla. Please check your inbox and follow the instructions.',
            type: 'warning',
          },
        },
      };
    }

    const auth = { token: state.auth.token, isAuthenticated: true, user: response };
    persistData(auth);

    return {
      ...state,
      auth,
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: false, message: '', type: '' },
      },
    } as IStore;
  },
  slack: (state: IStore) => async (token: string, language: string) => {
    const response = await request.get(api.slack(token, language));

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: response.error.body.data[0].messages[0].message,
            type: 'error',
          },
        },
      };
    }

    const { jwt, user } = response;
    const auth = { token: jwt, isAuthenticated: true, user };
    persistData(auth);

    return {
      ...state,
      auth,
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: false, message: '', type: '' },
      },
    };
  },
  microsoft: (state: IStore) => async (token: string, language: string) => {
    const response = await request.get(api.microsoft(token, language));

    if (response.error) {
      return {
        ...state,
        misc: {
          loading: false,
          notification: {
            status: true,
            message: response.error.body.data[0].messages[0].message,
            type: 'error',
          },
        },
      };
    }

    const { jwt, user } = response;
    const auth = { token: jwt, isAuthenticated: true, user };
    persistData(auth);

    return {
      ...state,
      auth,
      misc: {
        ...state.misc,
        loading: false,
        notification: { status: false, message: '', type: '' },
      },
    };
  },
};
